import { useState, type ReactNode } from "react"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Link from "@/v2-backoffice-client/components/link"
import UsageChurnRiskBadge from "@/v2-backoffice-client/usage/scope/UsageChurnRiskBadge"
import {
  buildCustomerActivityLink,
  type PortfolioCustomerSummary,
} from "@/v2-backoffice-client/portfolio/portfolio.utils"

export type PortfolioCustomerListRow = {
  customer: PortfolioCustomerSummary
  /** Right-aligned, caller-specific detail (labeled numbers, badges…). */
  detail?: ReactNode
}

export type PortfolioCustomerListDialogProps = {
  open: boolean
  onOpenChange(open: boolean): void
  title: string
  /** One plain sentence explaining what the list and its numbers mean. */
  description: string
  /** Right-aligned header labelling the detail column, when rows carry one. */
  detailHeader?: ReactNode
  rows: PortfolioCustomerListRow[]
}

// The "show everything" dialog behind segments, movers and other capped
// lists: every affected customer with search, churn badge and a deep link
// to the account's Activity tab. Callers label their own detail column so
// no number ever shows up without a meaning.
function PortfolioCustomerListDialog(props: PortfolioCustomerListDialogProps) {
  const { open, onOpenChange, title, description, detailHeader, rows } = props

  const [search, setSearch] = useState("")
  const searchTerm = search.trim().toLowerCase()
  const visible =
    searchTerm === ""
      ? rows
      : rows.filter((row) =>
          row.customer.name.toLowerCase().includes(searchTerm),
        )

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setSearch("")
        onOpenChange(next)
      }}
    >
      <Dialog.Content className="w-11/12 max-w-screen-md" align="top">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>

        <div className="flex flex-col gap-2">
          <Input
            type="search"
            size="sm"
            placeholder="Search customers…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {detailHeader != null && visible.length > 0 && (
            <div className="flex items-center justify-end text-xs text-muted-foreground">
              {detailHeader}
            </div>
          )}

          {visible.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">
              No customers match the search.
            </p>
          ) : (
            <ul className="flex max-h-[55vh] flex-col gap-1 overflow-y-auto pr-1">
              {visible.map((row) => (
                <li
                  key={row.customer.externalCustomerId}
                  className="flex items-center gap-2 rounded-md px-1.5 py-1 hover:bg-muted/40"
                >
                  <Link
                    to={buildCustomerActivityLink(
                      row.customer.externalCustomerId,
                    )}
                    iconSrc={null}
                    className="min-w-0 flex-1 truncate font-medium"
                  >
                    {row.customer.name}
                  </Link>
                  <UsageChurnRiskBadge
                    level={row.customer.summary.churnRisk.level}
                    reasons={row.customer.summary.churnRisk.reasons}
                    className="shrink-0"
                  />
                  {row.detail != null && (
                    <span className="flex shrink-0 items-center gap-2">
                      {row.detail}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  )
}

export default PortfolioCustomerListDialog
